// ExportBookModal.tsx
// NOTE: Update frontend/src/__tests__/ExportBookModal.test.tsx and run
// `../scripts/test-frontend.sh ExportBookModal` when modifying this modal.
import React from 'react';
import type { TourProps } from 'antd';
import { Modal, Button, Select, Tooltip, Checkbox, Segmented } from 'antd';
import {InfoCircleOutlined, QuestionCircleOutlined, DownOutlined} from '@ant-design/icons';
import type { RecentExportEntry } from './types';
import ModalInlineNotice from './components/ModalInlineNotice';
import {
  getTargetLanguageFromPair,
  splitLanguagePair,
  stripProofreadLabel,
} from './utils/languagePairs';
import { canonicalizeLanguageKey } from './utils/languageIdentity';
import { resolveFrontendApiBase } from './utils/apiBase';
import AppTour, { renderAppTourPanel } from './tours/AppTour';
import { tourTarget, useGuidedTour } from './tours/useGuidedTour';

const API_BASE = resolveFrontendApiBase();
const MAX_COLLAPSED_WARNING_CHAPTERS = 8;

interface ExportBookModalProps {
  isVisible: boolean;
  onCancel: () => void;
  onExport: (payload: ExportBookRequestPayload) => void;
  exportVersion: string;
  setExportVersion: (version: string) => void;
  exportFormat: string;
  setExportFormat: (format: string) => void;
  uploadType: string;
  includeImages: boolean;
  setIncludeImages: (include: boolean) => void;
  proofreadOptionProps: {
    value: string;
    disabled: boolean;
    text: string;
  };
  exportOrientation: 'horizontal' | 'vertical';
  setExportOrientation: (orientation: 'horizontal' | 'vertical') => void;
  book: {
    translation_pairs?: string[];
  };
  originalLanguageLabel?: string;
  formatLangDisplayText: (pair: string) => string;
  proofreadOptions: string[];
  showWarning: boolean;
  untranslatedChapters: string[];
  unproofreadChapters: string[];
  simpleMode?: boolean;
  recentExports: RecentExportEntry[];
  onDownloadRecentExport: (entry: RecentExportEntry) => Promise<void>;
  tourUserId?: string;
}

export interface ExportBookRequestPayload {
  version: string;
  format: string;
  includeImages: boolean;
  writingOrientation: 'horizontal' | 'vertical';
}

const ExportBookModal: React.FC<ExportBookModalProps> = ({
  isVisible,
  onCancel,
  onExport,
  exportVersion,
  setExportVersion,
  exportFormat,
  setExportFormat,
  uploadType,
  includeImages,
  setIncludeImages,
  proofreadOptionProps,
  exportOrientation,
  setExportOrientation,
  book,
  originalLanguageLabel = 'Original',
  formatLangDisplayText,
  proofreadOptions,
  showWarning,
  untranslatedChapters,
  unproofreadChapters,
  simpleMode = false,
  recentExports = [],
  onDownloadRecentExport,
  tourUserId,
}) => {
  const [refreshingLockKey, setRefreshingLockKey] = React.useState<string | null>(null);
  const [expandedWarningLists, setExpandedWarningLists] = React.useState<Record<string, boolean>>({});
  const hasSelectedVersion = Boolean((exportVersion || '').trim());

  React.useEffect(() => {
    if (!isVisible || !simpleMode) {
      return;
    }
    if (!exportVersion || exportVersion === 'Original' || !(exportVersion || '').includes('Proofread')) {
      return;
    }
    setExportVersion('Original');
  }, [isVisible, simpleMode, exportVersion, setExportVersion]);

  const targetLanguage = React.useMemo(() => {
    if (!exportVersion || exportVersion === 'Original' || exportVersion === 'Proofread') return '';
    return getTargetLanguageFromPair(exportVersion) || '';
  }, [exportVersion]);

  // Determine if the selected export target language is German
  const isGermanTarget = React.useMemo(() => targetLanguage === 'German', [targetLanguage]);
  const isJapaneseTarget = React.useMemo(() => targetLanguage === 'Japanese', [targetLanguage]);
  const showOrientationSelector = isJapaneseTarget;
  const orientationDisabled = exportFormat === 'ePub';
  const isProofreadExport = !simpleMode && (exportVersion === 'Proofread' || exportVersion.includes('Proofread'));
  const isProofreadTranslationExport = !simpleMode && exportVersion !== 'Proofread' && exportVersion.includes('Proofread');

  const formatExportLabel = React.useCallback((entry: RecentExportEntry): string => {
    const versionLabel = (entry.version || 'Export').trim();
    const formatLabel = (entry.format || '').trim();
    let displayVersion = versionLabel;
    const { targetLang } = splitLanguagePair(stripProofreadLabel(versionLabel));
    if (targetLang) {
      displayVersion = targetLang || versionLabel;
    }
    if (!formatLabel) {
      return displayVersion || 'Export';
    }
    return `${displayVersion} (${formatLabel})`;
  }, []);

  const formatExportDate = React.useCallback((entry: RecentExportEntry): string => {
    const raw = entry.completedAt;
    if (!raw) {
      return 'Unknown date';
    }
    const parsed = new Date(raw);
    if (Number.isNaN(parsed.getTime())) {
      return 'Unknown date';
    }
    return parsed.toLocaleString();
  }, []);

  const selectedExportLanguageKey = React.useMemo(() => {
    const selectedVersion = (exportVersion || '').trim();
    if (!selectedVersion) {
      return '';
    }
    const { targetLang } = splitLanguagePair(selectedVersion);
    return canonicalizeLanguageKey(targetLang || stripProofreadLabel(selectedVersion));
  }, [exportVersion]);

  const matchingRecentExports = React.useMemo(() => {
    if (!selectedExportLanguageKey) {
      return [];
    }
    return recentExports.filter(entry => {
      const candidates = [entry.languageKey, entry.version]
        .map(value => String(value || '').trim())
        .filter(Boolean);
      return candidates.some(candidate => {
        const { targetLang } = splitLanguagePair(candidate);
        return canonicalizeLanguageKey(targetLang || stripProofreadLabel(candidate)) === selectedExportLanguageKey;
      });
    });
  }, [recentExports, selectedExportLanguageKey]);

  const renderChapterWarningList = React.useCallback((listKey: string, chapters: string[]) => {
    const isExpanded = Boolean(expandedWarningLists[listKey]);
    const hiddenCount = Math.max(chapters.length - MAX_COLLAPSED_WARNING_CHAPTERS, 0);
    const visibleChapters = isExpanded ? chapters : chapters.slice(0, MAX_COLLAPSED_WARNING_CHAPTERS);

    return (
      <div className="export-book-modal-warning-list-block">
        <ul className={`export-book-modal-warning-list${isExpanded ? ' export-book-modal-warning-list--expanded' : ''}`}>
          {visibleChapters.map((chapterName, index) => (
            <li key={`${chapterName}-${index}`}>{chapterName}</li>
          ))}
        </ul>
        {hiddenCount > 0 && (
          <Button
            type="link"
            size="small"
            className="export-book-modal-warning-list-toggle"
            onClick={() => setExpandedWarningLists(current => ({
              ...current,
              [listKey]: !isExpanded,
            }))}
          >
            {isExpanded ? 'Show fewer chapters' : `Show ${hiddenCount} more chapter${hiddenCount === 1 ? '' : 's'}`}
          </Button>
        )}
      </div>
    );
  }, [expandedWarningLists]);

  const handleDownload = async (entry: RecentExportEntry) => {
    const exportId = entry.exportId;
    if (!exportId) {
      return;
    }
    setRefreshingLockKey(exportId);
    try {
      await onDownloadRecentExport(entry);
    } finally {
      setRefreshingLockKey(null);
    }
  };

  const selectedVersionLabel = hasSelectedVersion
    ? exportVersion === 'Original'
      ? originalLanguageLabel
      : formatLangDisplayText(exportVersion)
    : '';
  const footerSummary = (
    <div className="translation-modal-footer-summary" aria-live="polite">
      {hasSelectedVersion ? (
        <>
          <span>{selectedVersionLabel}</span>
          <span className="translation-modal-footer-summary__separator">·</span>
          <span>{exportFormat}</span>
          {showOrientationSelector ? (
            <>
              <span className="translation-modal-footer-summary__separator">·</span>
              <span>{orientationDisabled ? 'Horizontal layout' : `${exportOrientation} layout`}</span>
            </>
          ) : null}
        </>
      ) : (
        <span className="export-book-modal-footer-blocker">Select a version to continue</span>
      )}
    </div>
  );
  const exportTour = useGuidedTour({
    userId: tourUserId,
    apiBase: API_BASE,
    flag: 'exportBookModalTourSeen',
    enabled: isVisible,
    autoStart: true,
  });
  const exportTourSteps = React.useMemo<TourProps['steps']>(() => ([
    {
      title: 'Choose export setup',
      description: renderAppTourPanel('Select the manuscript version and file format you want to generate.'),
      target: tourTarget('export-book-setup'),
    },
    {
      title: 'Set format options',
      description: renderAppTourPanel('When available, choose image handling or Japanese text orientation for this export.'),
      target: tourTarget('export-book-options', 'export-book-setup'),
    },
    {
      title: 'Review readiness',
      description: renderAppTourPanel('Warnings call out untranslated chapters, partial proofreads, and format-specific publishing caveats before export.'),
      target: tourTarget('export-book-readiness'),
    },
    {
      title: 'Download recent exports',
      description: renderAppTourPanel('Completed exports stay here with download links you can refresh if a link expires.'),
      target: tourTarget('export-book-recent', 'export-book-readiness'),
    },
    {
      title: 'Create the export',
      description: renderAppTourPanel('Start the export once the version and readiness checks look correct.'),
      target: tourTarget('export-book-submit', 'export-book-readiness'),
    },
  ]), []);

  return (
    <>
    <Modal
      title={
        <div className="translation-modal-titlebar">
          <span className="translation-modal-titlebar__title">Export Book</span>
          <Button
            type="text"
            size="small"
            icon={<QuestionCircleOutlined />}
            onClick={exportTour.start}
            data-tour-id="export-book-tour-trigger"
            className="translation-modal-titlebar__tour app-tour-trigger"
          >
            Take a tour
          </Button>
        </div>
      }
      visible={isVisible}
      onCancel={onCancel}
      centered
      className="translation-modal-footer-stable export-book-modal"
      width="min(820px, calc(100vw - 32px))"
      bodyStyle={{
        maxHeight: 'min(76vh, calc(100vh - 220px))',
        overflowY: 'auto',
        overflowX: 'hidden',
        padding: '16px 16px 12px',
      }}
      footer={[
        <React.Fragment key="export-summary">
          {footerSummary}
        </React.Fragment>,
        <Button key="cancel" onClick={onCancel} data-testid="tutorial:export:cancel">Cancel</Button>,
        <Button
          key="export"
          type="primary"
          onClick={() =>
            onExport({
              version: exportVersion,
              format: exportFormat,
              includeImages: simpleMode && uploadType !== 'Word' ? false : includeImages,
              writingOrientation: simpleMode ? 'horizontal' : (orientationDisabled ? 'horizontal' : exportOrientation),
            })
          }
          disabled={!exportVersion}
          data-tour-id="export-book-submit"
          data-testid="tutorial:export:start"
        >
          Export
        </Button>,
      ]}
    >
      <div className="translation-modal-body export-book-modal-body" data-testid="tutorial:export:modal">
        <section
          className="translation-modal-block translation-modal-block--scope"
          aria-labelledby="export-book-setup-heading"
          data-tour-id="export-book-setup"
          data-testid="tutorial:export:setup"
        >
          <div className="translation-modal-block-header">
            <div>
              <h3 id="export-book-setup-heading">Export setup</h3>
              <p>Choose the book version and file type for this export.</p>
            </div>
          </div>
          <div className="export-book-modal-setup-grid">
            <div className="translation-quality-control">
              <span className="translation-modal-kicker">Version</span>
              <Select
                data-testid="tutorial:export:version"
                value={exportVersion}
                placeholder="Select export version"
                suffixIcon={<span className="ant-select-suffix"><DownOutlined style={{ color: '#1677ff', pointerEvents: 'none' }}/></span>}
                onChange={value => {
                  setExportVersion(value);
                  // When export version changes, update warnings in parent component via useEffect
                }}
                style={{ width: '100%' }}
                listHeight={400}
                >
                  <Select.Option value="">Select version</Select.Option>
                  <Select.Option value="Original">{originalLanguageLabel}</Select.Option>
                  {!simpleMode && (
                    <Select.Option value={proofreadOptionProps.value} disabled={proofreadOptionProps.disabled}>
                      {proofreadOptionProps.text}
                    </Select.Option>
                  )}
                  {book.translation_pairs && book.translation_pairs.map(pair => (
                    <Select.Option key={pair} value={pair}>{formatLangDisplayText(pair)}</Select.Option>
                  ))}
                  {!simpleMode && proofreadOptions.map(option => (
                    <Select.Option key={option} value={option}>{formatLangDisplayText(option)}</Select.Option>
                  ))}
                </Select>
              </div>
            <div className="translation-quality-control">
              <span className="translation-modal-kicker translation-quality-label">
                Format
                <Tooltip title="ScribeShadow can export books uploaded as Word documents only as Word documents. Books uploaded as ePubs can be exported as ePubs or Word documents. The ePub to Word conversion may lose some formatting.">
                  <QuestionCircleOutlined style={{ cursor: 'help' }} />
                </Tooltip>
              </span>
              <Select
                data-testid="tutorial:export:format"
                value={exportFormat}
                suffixIcon={<span className="ant-select-suffix"><DownOutlined style={{ color: '#1677ff', pointerEvents: 'none' }}/></span>}
                onChange={setExportFormat}
                style={{ width: '100%' }}
              >
                {uploadType === 'Word' && <Select.Option value="Word">Word</Select.Option>}
                {uploadType === 'ePub' && <Select.Option value="ePub">ePub</Select.Option>}
                {uploadType === 'ePub' && <Select.Option value="Word">Word</Select.Option>}
              </Select>
            </div>
          </div>
        </section>

        {((exportFormat === 'Word' && (!simpleMode || uploadType === 'Word')) || (!simpleMode && showOrientationSelector)) ? (
          <section className="translation-modal-block" aria-labelledby="export-book-options-heading" data-tour-id="export-book-options">
            <div className="translation-modal-block-header">
              <div>
                <h3 id="export-book-options-heading">Options</h3>
                <p>Choose format-specific export handling.</p>
              </div>
            </div>
            <div className="export-book-modal-option-list">
              {exportFormat === 'Word' && (!simpleMode || uploadType === 'Word') && (
                <div className="translation-quality-control export-book-modal-option-row">
                  <Checkbox
                    checked={includeImages}
                    onChange={(e) => setIncludeImages(e.target.checked)}
                  >
                    {uploadType === 'ePub' ? 'Include images from ePub embedded in Word document' : 'Include images'}
                  </Checkbox>
                  <div className="translation-quality-helper">Turn off to export a Word document without images. Saved chapters and images stay unchanged.</div>
                </div>
              )}

              {!simpleMode && showOrientationSelector && (
                <div className="translation-quality-control export-book-modal-option-row">
                  <span className="translation-modal-kicker">Japanese Text Orientation</span>
                  <Segmented
                    size="small"
                    value={orientationDisabled ? 'horizontal' : exportOrientation}
                    onChange={(value) => setExportOrientation(value as 'horizontal' | 'vertical')}
                    options={[
                      { label: 'Horizontal (横書き)', value: 'horizontal' },
                      { label: 'Vertical (縦書き)', value: 'vertical' },
                    ]}
                    aria-label="Japanese text orientation for this export"
                    disabled={orientationDisabled}
                  />
                  <div className="translation-quality-helper">Applies to this export only.</div>
                  {orientationDisabled && (
                    <div className="translation-readiness-support-note translation-readiness-support-note--warning">
                      Vertical layout is unavailable for EPUB exports.
                    </div>
                  )}
                </div>
              )}
            </div>
          </section>
        ) : null}

        <section className="translation-modal-block" aria-labelledby="export-book-readiness-heading" data-tour-id="export-book-readiness" data-testid="tutorial:export:warnings">
          <div className="translation-modal-block-header">
            <div>
              <h3 id="export-book-readiness-heading">Readiness</h3>
              <p>Review export blockers and known storefront caveats before starting.</p>
            </div>
          </div>
          <div className="translation-readiness-panels">
            <div className="translation-readiness-panel export-book-modal-readiness-list">
              {!hasSelectedVersion && (
                <ModalInlineNotice
                  tone="warning"
                  message="Please select a version to continue."
                />
              )}

              {exportFormat === 'ePub' && (
                <ModalInlineNotice
                  tone="info"
                  message="Any chapters from the original ePub that were not translated by ScribeShadow will remain in the ePub in their original form."
                />
              )}

              {exportVersion !== "Original" && isGermanTarget && (
                <ModalInlineNotice
                  tone="warning"
                  message={
                    <>
                      <InfoCircleOutlined /> <strong>Important Note About Amazon's German Spellcheck:</strong> Amazon's spellchecker often flags a large number of "errors," in German text. These are false positives and not actual spelling mistakes. This behavior stems from limitations in the spellchecker's handling of the German language.
                    </>
                  }
                />
              )}

              {isProofreadExport && (
                <ModalInlineNotice
                  tone="info"
                  message={
                    <>
                      <InfoCircleOutlined /> Proofread exports use proofread chapters where available and unproofread text for the rest.
                    </>
                  }
                />
              )}

              {showWarning && hasSelectedVersion && isProofreadExport && unproofreadChapters.length > 0 && (
                <ModalInlineNotice
                  tone="warning"
                  message={
                    <div>
                      <div>
                        Warning: The following chapters are not proofread and will export with{' '}
                        {isProofreadTranslationExport ? 'unproofread translated text' : 'original text'}:
                      </div>
                      {renderChapterWarningList('unproofread', unproofreadChapters)}
                    </div>
                  }
                />
              )}

              {showWarning && hasSelectedVersion && isProofreadTranslationExport && untranslatedChapters.length > 0 && (
                <ModalInlineNotice
                  tone="warning"
                  message={
                    <div>
                      <div>Warning: The following chapters do not have translation content:</div>
                      <div>
                        {exportFormat === 'ePub'
                          ? 'For EPUB exports they remain in the source language.'
                          : 'For Word/PDF translated exports they are omitted.'}
                      </div>
                      {renderChapterWarningList('proofread-untranslated', untranslatedChapters)}
                    </div>
                  }
                />
              )}

              {showWarning && hasSelectedVersion && exportVersion !== "Original" && !isProofreadExport && (
                <ModalInlineNotice
                  tone="warning"
                  message={
                    <div>
                      <div>Warning: Some chapters are not translated for the selected language pair:</div>
                      {renderChapterWarningList('untranslated', untranslatedChapters)}
                    </div>
                  }
                />
              )}

              {hasSelectedVersion && !showWarning && !isProofreadExport && !(exportVersion !== "Original" && isGermanTarget) && exportFormat !== 'ePub' && (
                <ModalInlineNotice
                  tone="success"
                  message={`Ready to export ${selectedVersionLabel} as ${exportFormat}.`}
                />
              )}
            </div>
          </div>
        </section>

        {matchingRecentExports.length > 0 && (
          <section className="translation-modal-block" aria-labelledby="export-book-recent-heading" data-tour-id="export-book-recent" data-testid="tutorial:export:recent">
            <div className="translation-modal-block-header">
              <div>
                <h3 id="export-book-recent-heading">Recent exports</h3>
                <p>Download previously completed exports when a link is still available.</p>
              </div>
            </div>
            <div className="export-book-modal-recent-grid">
              {matchingRecentExports.map(entry => (
                <div key={entry.exportId} className="export-book-modal-recent-row">
                  <div>
                    <strong>{formatExportLabel(entry)}</strong>
                    <span>
                      {entry.isStale
                        ? 'Translation changed after this file was created'
                        : `Created ${formatExportDate(entry)}`}
                    </span>
                  </div>
                  <Button
                    type={entry.isStale ? 'default' : 'primary'}
                    size="small"
                    className="export-book-modal-recent-download"
                    onClick={() => {
                      if (entry.isStale) {
                        onExport({
                          version: exportVersion,
                          format: exportFormat,
                          includeImages,
                          writingOrientation: exportOrientation,
                        });
                        return;
                      }
                      void handleDownload(entry);
                    }}
                    disabled={entry.isStale ? !hasSelectedVersion : !entry.exportId}
                    loading={refreshingLockKey === entry.exportId}
                  >
                    {entry.isStale ? 'Re-export' : 'Download'}
                  </Button>
                </div>
              ))}
            </div>
          </section>
        )}
      </div>
    </Modal>
    <AppTour
      open={exportTour.open}
      steps={exportTourSteps}
      current={exportTour.current}
      onChange={exportTour.setCurrent}
      onClose={exportTour.close}
      onFinish={exportTour.close}
      zIndex={2400}
    />
    </>
  );
};

export default ExportBookModal;
